import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { showToast } from "../../../../components/common/notification/Notification";
import PostWizard from "./PostWizard";

const EditPostForm = () => {
  const { Id } = useParams();
  const [postData, setPostData] = useState(null);
  const [services, setServices] = useState([]);
  const [states, setStates] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const user = useSelector((state) => state.auth.user);
  const token = user?.refreshToken;
  const navigate = useNavigate();

  // Fetch dropdown data and post data, then map the saved names to option ids
  useEffect(() => {
    const load = async () => {
      try {
        const [serviceRes, stateRes] = await Promise.all([
          axios.get(`${baseUrl}/all/services`),
          axios.post(
            `${baseUrl}/admin/states/1`,
            { page_number: 1, page_size: 1000, order_by: "ASC" },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);
        const serviceList = serviceRes.data.services || [];
        const stateList = stateRes.data.states || [];
        setServices(serviceList);
        setStates(stateList);

        if (!Id) return;
        const res = await axios.get(`${baseUrl}/property/lead/id/${Id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data.data;
        setPostData(data);

        const matchedService = serviceList.find((s) => s.name === data.service);
        const matchedState = stateList.find((s) => s.name === data.state_name);
        let matchedCity = null;
        if (matchedState) {
          const cityRes = await axios.post(
            `${baseUrl}/admin/cities/${matchedState.id}`,
            { page_number: 1, page_size: 1000, order_by: "ASC" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          matchedCity = (cityRes.data.cities || []).find((c) => c.name === data.city_name) || null;
        }
        setInitialValues({
          service_id: matchedService ? matchedService.id : "",
          service: data.service || "",
          state: matchedState ? matchedState.id : "",
          state_name: data.state_name || "",
          city: matchedCity ? matchedCity.id : "",
          city_name: data.city_name || "",
          description: data.description || "",
        });
      } catch (err) {
        console.error("Error loading post:", err);
        showToast("Failed to load post", { type: "error" });
      }
    };
    load();
  }, [baseUrl, token, Id]);

  // Called from the review step
  const handleFinalSubmit = async (values) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const currentUserId = user?.id || user?.user?.id || user?.userDetails?.id;
      const payload = {
        id: postData.id,
        service: values.service,
        state_name: values.state_name,
        city_name: values.city_name,
        description: values.description,
        property_status_id: postData?.property_status_id || 1,
        property_type_id: postData?.property_type_id || 1,
        purpose_id: postData?.purpose_id || 1,
        user_id: currentUserId,
      };

      const response = await axios.put(
        `${baseUrl}/property/lead/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.statusType === "SUCCESS") {
        showToast("Post updated successfully", { type: "success" });
        // isSubmitting stays true until the redirect, which also lifts the unsaved-changes warning
        setTimeout(() => {
          navigate("/post");
        }, 1500);
      } else {
        setIsSubmitting(false);
        showToast(response.data.text || "Failed to update Post data", { type: "error" });
      }
    } catch (err) {
      setIsSubmitting(false);
      showToast("Failed to update Post data", { type: "error" });
      console.log(err);
    }
  };

  if (!initialValues) {
    return (
      <div className="fk-page" aria-busy="true">
        <div className="fk-skel" style={{ height: 28, width: "30%", marginBottom: 16 }} />
        <div className="fk-skel" style={{ height: 320 }} />
      </div>
    );
  }

  return (
    <PostWizard
      mode="edit"
      initialValues={initialValues}
      services={services}
      states={states}
      onSubmit={handleFinalSubmit}
      isSubmitting={isSubmitting}
    />
  );
};

export default EditPostForm;
